import { apiGet, apiPost, API_BASE, getAuthHeaders } from '@/utils/api';
import type {
  AdminGradeFilters,
  AdminGradeFiltersResponse,
  GradeOverviewRow,
  StudentStatusRow,
  ClassAverageRow,
  GradeAnalyticsSummary,
  GradeOverrideRecord,
  MatiereGradeSummary,
  StudentGradeDetail,
} from '@/types/admin-grades.types';

function buildParams(filters: AdminGradeFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.campusIds?.length) params.append('campusIds', filters.campusIds.join(','));
  if (filters.academicYearId) params.append('academicYearId', filters.academicYearId);
  if (filters.semesterNumbers?.length) params.append('semesterNumbers', filters.semesterNumbers.join(','));
  if (filters.filiereIds?.length) params.append('filiereIds', filters.filiereIds.join(','));
  if (filters.niveaux?.length) params.append('niveaux', filters.niveaux.join(','));
  if (filters.search) params.append('search', filters.search);
  return params;
}

export async function getAdminGradeFilters(): Promise<AdminGradeFiltersResponse> {
  return apiGet<AdminGradeFiltersResponse>('/admin-grades/filters');
}

export async function getGradeAnalytics(filters: AdminGradeFilters): Promise<GradeAnalyticsSummary> {
  const params = buildParams(filters);
  return apiGet<GradeAnalyticsSummary>(`/admin-grades/analytics?${params.toString()}`);
}

export async function getGradeOverview(filters: AdminGradeFilters): Promise<GradeOverviewRow[]> {
  const params = buildParams(filters);
  return apiGet<GradeOverviewRow[]>(`/admin-grades/overview?${params.toString()}`);
}

export async function getStudentStatusReport(filters: AdminGradeFilters): Promise<StudentStatusRow[]> {
  const params = buildParams(filters);
  return apiGet<StudentStatusRow[]>(`/admin-grades/student-status?${params.toString()}`);
}

export async function getClassAverages(filters: AdminGradeFilters): Promise<ClassAverageRow[]> {
  const params = buildParams(filters);
  return apiGet<ClassAverageRow[]>(`/admin-grades/class-averages?${params.toString()}`);
}

export async function overrideGrade(studentComponentGradeId: string, data: { overrideScore: number; reason: string }): Promise<void> {
  return apiPost<void>(`/admin-grades/override/${studentComponentGradeId}`, data);
}

export async function createAndOverrideGrade(data: {
  gradeComponentConfigId: string;
  studentId: string;
  groupeId: string;
  overrideScore: number;
  reason: string;
}): Promise<{ studentComponentGradeId: string }> {
  return apiPost<{ studentComponentGradeId: string }>('/admin-grades/override-create', data);
}

export async function getOverrideHistory(studentComponentGradeId: string): Promise<GradeOverrideRecord[]> {
  return apiGet<GradeOverrideRecord[]>(`/admin-grades/override-history/${studentComponentGradeId}`);
}

export async function getMatiereSummary(filters: AdminGradeFilters): Promise<MatiereGradeSummary[]> {
  const params = buildParams(filters);
  return apiGet<MatiereGradeSummary[]>(`/admin-grades/matiere-summary?${params.toString()}`);
}

export async function getStudentGradeDetail(studentId: string, filters: AdminGradeFilters): Promise<StudentGradeDetail[]> {
  const params = buildParams(filters);
  return apiGet<StudentGradeDetail[]>(`/admin-grades/student-detail/${studentId}?${params.toString()}`);
}

// ===== Export Functions =====

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getDateSuffix(): string {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
}

function getExportHeaders(): Record<string, string> {
  // Use getAuthHeaders but remove Content-Type for blob downloads
  const headers = getAuthHeaders();
  delete headers['Content-Type'];
  return headers;
}

export async function exportGradeExcel(filters: AdminGradeFilters): Promise<void> {
  const params = buildParams(filters);
  const response = await fetch(`${API_BASE}/admin-grades/export/excel?${params.toString()}`, {
    method: 'GET',
    headers: getExportHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Erreur lors de l'export Excel");
  }
  const blob = await response.blob();
  downloadBlob(blob, `rapport-notes-${getDateSuffix()}.xlsx`);
}

export async function exportGradePdf(filters: AdminGradeFilters): Promise<void> {
  const params = buildParams(filters);
  const response = await fetch(`${API_BASE}/admin-grades/export/pdf?${params.toString()}`, {
    method: 'GET',
    headers: getExportHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Erreur lors de l'export PDF");
  }
  const blob = await response.blob();
  downloadBlob(blob, `rapport-notes-${getDateSuffix()}.pdf`);
}

export async function exportStudentGradeReport(
  studentId: string,
  filters: AdminGradeFilters,
  scope: 'current' | 'all',
): Promise<void> {
  const params = buildParams(filters);
  params.append('scope', scope);
  const response = await fetch(
    `${API_BASE}/admin-grades/export/student-report/${studentId}?${params.toString()}`,
    {
      method: 'GET',
      headers: getExportHeaders(),
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Erreur lors de l'export du rapport");
  }
  const blob = await response.blob();
  downloadBlob(blob, `releve-notes-${getDateSuffix()}.pdf`);
}
